import { TranslocoService } from '@jsverse/transloco';

/**
 * localStorage key holding the language the user last picked.
 *
 * The profile language (`user_settings_language`) only exists once someone is
 * logged in, so without this key every anonymous page load falls back to the
 * default language. That is what made the Longines-ID / Cognito hosted UI
 * always render in German: `CognitoAuthService.login()` passes the active
 * transloco language, and on the sign-in page that was always `de` (HIP-43).
 */
export const APP_LANGUAGE_KEY = 'appLanguage';

/**
 * Remembers the picked language across reloads and logouts. Never throws —
 * localStorage can be unavailable (private mode, blocked storage).
 */
export function storeLanguage(lang: string): void {
    if (!lang) {
        return;
    }
    try {
        localStorage.setItem(APP_LANGUAGE_KEY, lang);
    } catch {}
}

/**
 * The language the app should start in: the last picked one if it is still a
 * language the app offers, otherwise the configured default.
 */
export function resolveInitialLang(transloco: TranslocoService): string {
    const defaultLang = transloco.getDefaultLang();
    const availableLangs = transloco
        .getAvailableLangs()
        .map(lang => (typeof lang === 'string' ? lang : lang.id));
    let stored: string | null = null;
    try {
        stored = localStorage.getItem(APP_LANGUAGE_KEY);
    } catch {
        stored = null;
    }
    return stored && availableLangs.includes(stored) ? stored : defaultLang;
}
